import { FC, useMemo, useState } from "react";
import toast from "react-hot-toast";

import { Button } from "shared/ui/shadcn/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "shared/ui/shadcn/select";

import { useSnoozeDailyTodos } from "../data/queries";
import { describeDailyTodosFailure } from "../lib/data";
import { formatClock, getSnoozeSlots } from "../lib/schedule";

/**
 * Snooze today's reminder once (CC's "Snooze until"): the open half hours,
 * at least 30 minutes ahead and no later than 9:00 PM ET, shown in the
 * caller's own time.
 */
export const SnoozePicker: FC<{
  now: Date;
  onBack: () => void;
  onSnoozed: () => void;
}> = ({ now, onBack, onSnoozed }) => {
  const slots = useMemo(() => getSnoozeSlots(now), [now]);
  const [picked, setPicked] = useState<string>("");
  const snooze = useSnoozeDailyTodos();
  // A slot that closed while the picker was open is no longer picked.
  const value = slots.some((slot) => slot.toISOString() === picked)
    ? picked
    : "";

  const onSnooze = () => {
    const until = slots.find((slot) => slot.toISOString() === value);

    if (!until) {
      return;
    }

    snooze.mutate(until, {
      onSuccess: () => {
        toast.success(`Snoozed until ${formatClock(until)}`);
        onSnoozed();
      },
      onError: (error) =>
        toast.error(describeDailyTodosFailure(error, "Failed to snooze")),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Select value={value} onValueChange={setPicked}>
        <SelectTrigger aria-label="Snooze until" className="w-full">
          <SelectValue placeholder="Snooze until…" />
        </SelectTrigger>
        <SelectContent className="z-[200] max-h-[280px]">
          {slots.map((slot) => (
            <SelectItem key={slot.toISOString()} value={slot.toISOString()}>
              {formatClock(slot)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-black/60 typography-body-5">
        You can snooze once a day. When it ends, finish the list before you move
        on.
      </span>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onSnooze} disabled={!value} loading={snooze.isPending}>
          Snooze
        </Button>
      </div>
    </div>
  );
};
